import { safeSessionStorage } from './localStorage';

/**
 * Membership Referral attribution — carries the referral code from a member's
 * shareable link (e.g. /unlimited?ref=ABC12345) through to the Shopify checkout
 * call, so the backend can attribute the subscription to the referrer and issue
 * TrashieCash to both parties (see the Membership Referral integration guide).
 *
 * Per the integration guide the code is persisted in sessionStorage so it
 * survives in-app navigation before the friend completes checkout.
 */
const STORAGE_KEY = 'referralCode';

// Referral codes are short alphanumeric tokens (e.g. "ABC12345"). Reject
// anything else so a forged value can't smuggle unexpected characters into the
// checkout payload, and cap the length defensively.
const CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

const normalizeCode = (value) => {
  if (!value) return null;
  const code = String(value).trim();
  return CODE_PATTERN.test(code) ? code : null;
};

/** Returns the stored referral code, or null. */
export const getStoredReferralCode = () => {
  try {
    return normalizeCode(safeSessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

export const setReferralCode = (rawCode) => {
  const code = normalizeCode(rawCode);
  if (!code) return;
  try {
    safeSessionStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* silent */
  }
};

/**
 * Captures ?ref=<code> from the current URL into sessionStorage. Last-touch
 * overwrite — a newer referral link updates the stored value. Returns the
 * resulting stored code, falling back to whatever was already stored when the
 * URL has no param. Intended to be called on the referral landing page mount.
 */
export const captureReferralCodeFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = normalizeCode(params.get('ref'));
    if (code) {
      setReferralCode(code);
      return code;
    }
  } catch {
    /* silent */
  }
  return getStoredReferralCode();
};
